import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/site";

const FOUNDER_PHOTO = "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=85&w=1600";

export default function FounderSection() {
  return (
    <section className="relative bg-mist py-24 sm:py-32 overflow-hidden" data-testid="founder-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[40vw] h-[40vw] bg-emerald-brand/5 rounded-full blur-3xl" />
      </div>

      <div className="relative container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={FOUNDER_PHOTO}
              alt={BRAND.founder}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-navy/10" />
          </div>
          <div className="absolute -bottom-6 -right-6 hidden md:block bg-gold p-6 max-w-[240px] shadow-xl">
            <div className="overline text-navy/70">Founder</div>
            <div className="font-display font-extrabold text-navy text-2xl mt-1">{BRAND.founder}</div>
            <div className="text-xs text-navy/70 mt-1">Managing Director, NEPHRC</div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <div className="overline text-emerald-brand">Founder&apos;s Note</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-navy mt-4">
            &ldquo;We engineer outcomes — not just <span className="text-emerald-brand">deliverables</span>.&rdquo;
          </h2>
          <div className="mt-8 relative pl-8">
            <Quote className="absolute -left-1 top-0 text-gold" size={28} />
            <p className="text-lg text-slate-brand leading-relaxed">
              NEPHRC was founded with a single conviction: that India&apos;s next wave of infrastructure, hospitality and retail deserves consultancy partners who combine global engineering discipline with deep local understanding.
            </p>
            <p className="mt-4 text-base text-slate-brand leading-relaxed">
              Our work is judged not by drawings issued but by outcomes delivered — schools that stand for generations, hotels that operate at world-class standards, and corridors that move communities forward.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {[
              { k: "20+", v: "Years of leadership" },
              { k: "₹2,800Cr+", v: "Projects advised" },
              { k: "12 states", v: "Geographic footprint" },
            ].map((m) => (
              <div key={m.v} className="border-l-2 border-gold pl-4">
                <div className="font-display font-extrabold text-2xl text-navy">{m.k}</div>
                <div className="text-xs text-slate-brand mt-1">{m.v}</div>
              </div>
            ))}
          </div>

          <Link
            to="/founder"
            data-testid="founder-section-cta"
            className="mt-10 inline-flex items-center gap-2 text-navy font-semibold text-sm border-b border-gold pb-1 hover:gap-3 transition-all"
          >
            Read the founder&apos;s full message <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
